import {
    ICasinofiedAppLauncherService,
    ILaunchAppData,
    IOpenChatParams
} from './casinofied-app-launcher.service.interface';
import {ServiceBase} from '../../ServiceBase';
import {IServiceFactoryExtended} from '../../service-factory-extended.interface';
import {
    AppIdentifiers,
    IAppClosedTopicPayload,
    IAppMaximizedTopicPayload,
    IAppMinimizedTopicPayload,
    IOpenAppData,
    TriggerType
} from '@sparkware/uc-sdk-core';
import {CasinofiedAppFactory} from './casinofied-app-factory';
import {ICasinofiedApplication} from './apps/casinofied-application.interface';
import {STANDARD_TOPIC_HEADER} from '../message-broker/message-broker.consts';


export class CasinofiedAppLauncherService extends ServiceBase implements ICasinofiedAppLauncherService {

    private readonly _appFactory: CasinofiedAppFactory;
    private _runningApps: Record<string, ICasinofiedApplication> = {};

    constructor(services: IServiceFactoryExtended) {
        super(services);

        this._appFactory = new CasinofiedAppFactory(services);

        this._subscribeToTopics();
        this._subscribeToReactions();
        this._subscribeToSdkEvents();
        this._subscribeToBrowserEvents();

    }

    private _subscribeToTopics() {
        const {openApp} = this.services.messageBroker.channels.navigation.topics;
        openApp.subscribe(this._openAppHandler);

        const {appClosed, appMaximized, appMinimized} = this.services.messageBroker.channels.session.topics;
        appClosed.subscribe(this._appClosedHandler);
        appMinimized.subscribe(this._appMinimizedHandler);
        appMaximized.subscribe(this._appMaximizedHandler);
    }

    private _subscribeToReactions() {
        this.reaction(() => this.services.user.isAuthorized,
            (isAuthorized) => {
                const {loginSuccess, logoutSuccess} = this.services.messageBroker.channels.identity.topics;
                if(isAuthorized) {
                    loginSuccess.publish(STANDARD_TOPIC_HEADER, {});
                } else {
                    logoutSuccess.publish(STANDARD_TOPIC_HEADER, {});
                }
            });
    }

    private _subscribeToSdkEvents() {
        this.services.sdk.events.onBeforeOAuthStateChanged(this._onBeforeOAuthStateChangedHandler);
    }

    private _onBeforeOAuthStateChangedHandler = () => {
        this._closeAllApps("automatic_closure");
    }

    private _subscribeToBrowserEvents() {
        this.services.window.subscribeTo.unload(this._onBrowserUnload);
    }

    private _openAppHandler = async (payload: IOpenAppData): Promise<void> => {
       await this._openApp(payload, true);
    }

    private _onBrowserUnload = () => {
        this._closeAllApps("user_closed_app");
        this.services.casinofiedAnalytics.userClosedApp();
    }

    private async _openApp(payload: IOpenAppData, shouldReportOpenAppEvent: boolean): Promise<void> {
        const runningApp = this._runningApps[payload.appID];
        if(runningApp) {

            if(runningApp.isMinimized) {
                await runningApp.waitForAppToOpen();
                this.services.messageBroker.channels.session.topics.appMaximized.publish(STANDARD_TOPIC_HEADER, {
                    appID: runningApp.appId
                });
            }
            this.services.logger.info(`App ${payload.appID} is already open. Running apps: ${this._runningApps}`);
            return;
        }

        try {
            if(shouldReportOpenAppEvent) {
                this.services.casinofiedAnalytics.openAppRequest(payload);
            }

            const app = await this._appFactory.createApplication(payload);
            if (!app.isOpeningInNewWindow()) {
                // we keep running apps in memory to dispose them later, when they are closed
                // only for the apps that are not opened in another window
                this._runningApps[payload.appID] = app;
            }
            await app.open();

        } catch (err) {
            const app = this._runningApps[payload.appID];
            if(app) {
                delete this._runningApps[payload.appID];
                if(app.isMinimized) {
                    app.dispose();
                }
            }

            this.services.logger.error(`Failed to load app: ${payload.appID}`, err);
        }
    }

    private _appClosedHandler = (payload: IAppClosedTopicPayload) => {
        const runningApp = this._runningApps[payload.appID];
        if(runningApp) {
            delete this._runningApps[payload.appID];
            runningApp.dispose();
            if(!runningApp.isLoading) {
                this.services.casinofiedAnalytics.appClosed(payload);
            }
        }
    }

    private _appMinimizedHandler = (payload: IAppMinimizedTopicPayload): void => {
        const app = this._runningApps[payload.appID];
        if(app) {
            app.minimize(payload.appData);
        }
    }

    private _appMaximizedHandler = (payload: IAppMaximizedTopicPayload): void => {
        this._runningApps[payload.appID]?.maximize();
    }

    private _closeAllApps(closeReason: string) {
        Object.values(this._runningApps).forEach(app => {
            this.services.messageBroker.channels.session.topics.appClosed.publish(STANDARD_TOPIC_HEADER, {
                appID: app.appId,
                closeReason: closeReason,
                launchInfo: {
                    appSpecificParameters: app.openAppData.launchInfo?.appSpecificParameters,
                    businessCorrelationID: app.openAppData.launchInfo?.businessCorrelationID,
                    trigger: TriggerType.automaticFlow,
                    sourceAppID: app.appId,
                    sourceAppVersion: "",
                    channel: null
                }
            });
        });
        this._runningApps = {};
    }

    private _launchApp(params: ILaunchAppData, appIdentifier: AppIdentifiers): Promise<void> {
        const correlationId = params.businessCorrelationID || this.services.tools.getGuid();
        const clientVersion = this.services.sdk.getSkin().getVersion();
        return this._openApp({
            correlationID: correlationId,
            appID: appIdentifier,
            launchInfo: {
                containerID: this.services.tools.getGuid(),
                trigger: TriggerType.userSelection,
                appSpecificParameters: params.appSpecificParameters,
                businessCorrelationID: correlationId,
                channel: params.channel,
                sourceAppID: AppIdentifiers.CasinoClient,
                sourceAppVersion: clientVersion,
                clientVersion: clientVersion
            }
        }, false);
    }

    openChat(params: IOpenChatParams): Promise<void> {
        return this._launchApp(params, AppIdentifiers.CrChat);
    }

    openDepositWidget(params: ILaunchAppData): Promise<void> {
        return this._launchApp(params, AppIdentifiers.UnifiedCashier);
    }

    openCashoutWidget(params: ILaunchAppData): Promise<void> {
        return this._launchApp(params, AppIdentifiers.UnifiedCashout);
    }

    openBonusWheel(params: ILaunchAppData): Promise<void> {
        return this._launchApp(params, AppIdentifiers.CasinoBonusGameWidget);
    }

    openHelpWidget(params: ILaunchAppData): Promise<void> {
        return this._launchApp(params, AppIdentifiers.CRHelpCenter);
    }
}
